// @ts-nocheck
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useAuth } from 'wasp/client/auth';
import { useQuery, useAction } from 'wasp/client/operations';
import {
  getCurrentMealPlan,
  getUserMealPlans,
  getUsageStatus,
  generateMealPlan,
  searchRecipes,
  getUserProfile,
  getUserTasteProfile,
  getDiscoverRecipes,
  addRecipeToMealPlan,
  personalizeAndAddRecipeToMealPlan,
  previewPersonalizedRecipe,
} from 'wasp/client/operations';
import { useNavigate } from 'react-router-dom';
// @ts-nocheck
import { normalizeTier } from '../../utils/tierMapping';
import { useLikedRecipes } from '../../hooks/useLikedRecipes';
import RecipePageView from './RecipePage.view';
import type { PersonalizationPreviewResult } from "../../types/personalization";
import { Events } from "../../client/analytics/events";
import { trackEvent } from "../config/analytics";
import { waspApi } from "../config/waspIntegration";
import { toast } from "sonner";
import { trace } from "../../utils/trace";
import { usePersonalizedRecipesV2 } from "../../hooks/usePersonalizedRecipesV2";
import { useLayoutOptions } from "../components/frame/AppFrame";
import { UI_FLAGS as FLAGS } from "../../lib/flags";

// Business logic types
interface Recipe {
  id: string;
  title: string;
  description?: string;
  servings?: number;
  cookTime?: number;
  prepTime?: number;
  difficulty?: string;
  category?: string;
  tags?: string[];
  ingredients?: string[];
  instructions?: string[];
  nutrition?: {
    calories?: number;
    protein?: number;
    carbs?: number;
    fat?: number;
  };
  imageUrl?: string;
  isAiGenerated?: boolean;
  sourceUrl?: string;
}

interface MealPlan {
  id: string;
  recipes: Recipe[];
  weekRange?: string;
  generatedAt?: Date;
}

interface UserProfile {
  diet?: string;
  allergies?: string[];
  preferences?: string[];
}

interface RecipePageContainerProps {
  currentTier?: string;
  userName?: string;
}

let __recipePageStarted = false;

const cleanDescription = (text?: string): string | undefined => {
  if (!text) return undefined;
  // Remove common ingredient list markers and units
  const badPatterns = /(ingredients?:|\b(tsp|tbsp|cup|cups|oz|g|kg|ml|l)\b|\d+\s?(x|\/)|•|^-\s|^\d+\.)/i;
  // Take only the first sentence-like chunk
  const firstLine = text.split(/\n|\r|\.|\!|\?/).find((s) => s.trim().length > 0) || '';
  if (!firstLine) return undefined;
  if (badPatterns.test(firstLine)) return undefined;
  return firstLine.trim();
};

function upgradeSpoonacularImage(url: string | null): string | null {
  if (!url) return url;
  try {
    // Common Spoonacular pattern: .../recipeImages/<id>-<WxH>.<ext>
    return url.replace(/-(\d+)x(\d+)(?=\.[a-z]+($|\?))/i, '-636x393');
  } catch {
    return url;
  }
}

const normalizeDiscoverRecipe = (recipe: any) => {
  if (!recipe) return recipe;

  const primaryImage =
    recipe.image ||
    recipe.imageUrl ||
    recipe.heroImageUrl ||
    recipe.thumbnail ||
    recipe.hero_image ||
    null;

  const description =
    typeof recipe.description === "string" && recipe.description.trim().length > 0
      ? recipe.description
      : recipe.summary || undefined;

  const upgraded = upgradeSpoonacularImage(primaryImage);

  return {
    ...recipe,
    image: recipe.image || upgraded || primaryImage,
    imageUrl: recipe.imageUrl || upgraded || primaryImage,
    heroImageUrl: recipe.heroImageUrl || upgraded || primaryImage,
    description: cleanDescription(description),
    // Ensure mobile discover cards have enriched meta
    totalMinutes: recipe.totalMinutes ?? recipe.prepTime ?? recipe.readyInMinutes ?? null,
    prepTime: recipe.prepTime ?? recipe.readyInMinutes ?? recipe.totalMinutes ?? null,
    calories: recipe.calories ?? recipe.nutrition?.calories ?? recipe.nutritionInfo?.calories ?? null,
    cuisine: recipe.cuisine ?? (Array.isArray(recipe.cuisines) ? recipe.cuisines[0] : undefined),
    difficulty: recipe.difficulty ?? (recipe.readyInMinutes ? (recipe.readyInMinutes <= 20 ? 'easy' : (recipe.readyInMinutes <= 45 ? 'medium' : 'hard')) : undefined),
    ingredients: recipe.ingredients ?? recipe.extendedIngredients ?? [],
    tags: recipe.tags ?? recipe.diets ?? [],
  };
};

/**
 * RecipePage Container - Centralizes all data logic, hooks, and business logic
 * 
 * This centralizes:
 * - Wasp operations and data fetching (6+ operations)
 * - Recipe state management and search/filter logic
 * - Modal state management (replace, remove, details)
 * - Pagination and infinite scroll logic
 * - Analytics tracking and event handling
 * - Recipe collection management
 * - All event handlers and business logic
 */
export default function RecipePageContainer({
  currentTier = "demo",
  userName = "Chef"
}: RecipePageContainerProps) {
  const { setOptions } = useLayoutOptions();
  // Apply layout options once on mount; provider dedupes identical updates
  useEffect(() => {
    setOptions({ hideBreadcrumb: true, hideMainHeader: true, hideNavigation: false });
    return () => setOptions({ hideBreadcrumb: false, hideMainHeader: false, hideNavigation: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Wasp hooks for data fetching
  const { data: user } = useAuth();
  const isAuthenticated = !!user;
  const { data: currentMealPlan, refetch: refetchCurrentPlan } = useQuery(
    getCurrentMealPlan,
    {},
    { enabled: isAuthenticated },
  );
  const { refetch: refetchPlans } = useQuery(
    getUserMealPlans as any,
    {} as any,
    { enabled: false },
  );
  const { data: usageStatus } = useQuery(
    getUsageStatus,
    {},
    { enabled: isAuthenticated },
  );
  const { data: userProfile } = useQuery(
    getUserProfile,
    {},
    { enabled: isAuthenticated },
  );

  // Fetch user taste profile for personalization
  const { data: userTasteProfile } = useQuery(
    getUserTasteProfile,
    {},
    { enabled: isAuthenticated },
  );

  const navigate = useNavigate();

  // Enhanced state management for discover page
  const [searchQuery, setSearchQuery] = useState("");
  const [searchSeed, setSearchSeed] = useState(() =>
    Math.floor(Math.random() * 100),
  );
  const [refreshing, setRefreshing] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedDietTags, setSelectedDietTags] = useState<string[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // Add full pagination: take/skip for queries
  const [page, setPage] = useState(1);
  const take = 20;
  const [paginationCategory, setPaginationCategory] = useState<
    string | undefined
  >(undefined);
  const [seenRecipeIds, setSeenRecipeIds] = useState<Set<string>>(new Set());

  // Create parameters for getDiscoverRecipes with search
  const discoverParams = useMemo(() => {
    const params: any = { take, skip: (page - 1) * take };

    if (searchQuery.trim()) {
      params.q = searchQuery.trim();
    }

    // Use pagination category if set, otherwise use selected category
    const categoryToUse = paginationCategory || selectedCategory;
    if (categoryToUse) {
      params.category = categoryToUse;
    }

    if (selectedDietTags.length > 0) {
      params.dietTags = selectedDietTags;
    }

    if (user?.id) {
      params.userId = user.id;
    }

    return params;
  }, [searchQuery, selectedCategory, selectedDietTags, paginationCategory, user?.id]);

  const {
    data: recipesData,
    isLoading: loading,
    error,
  } = useQuery(getDiscoverRecipes, discoverParams, {
    enabled: isAuthenticated,
  });

// ⬇️ Put these near other hooks at the top of the component
// Ensure deck is stable (used later too)
const deck = React.useMemo(() => (recipesData?.recipes ?? []), [recipesData]);

// Create a stable, tiny debug payload so the effect only runs when it *meaningfully* changes
const debugSnapshot = React.useMemo(() => ({
  loading: !!loading,
  hasData: !!recipesData,
  dataLength: deck.length,
  // If discoverParams is an object, stringify once in a memo to stabilize deps
  paramsKey: JSON.stringify(discoverParams ?? null),
  isAuthenticated: !!isAuthenticated,
  // Keep error text stable
  errorMsg: error ? ((error as any)?.message || String(error)) : null,
}), [loading, recipesData, deck.length, discoverParams, isAuthenticated, error]);

// ✅ REPLACE your current logging effect with this
useEffect(() => {
  console.log("🔍 RecipePage: Query state changed", debugSnapshot);
}, [debugSnapshot]);

  // Reset pagination when search/category changes
  useEffect(() => {
    setPaginationCategory(undefined);
    setSeenRecipeIds(new Set());
    setHasMore(true);
  }, [searchQuery, selectedCategory, selectedDietTags]);

  const [creating, setCreating] = useState(false);
  const doGeneratePlan = useAction(generateMealPlan);
  const doAddRecipeToMealPlan = useAction(addRecipeToMealPlan);
  const doPersonalizeAdd = useAction(personalizeAndAddRecipeToMealPlan as any);
  const doPreviewPersonalization = useAction(previewPersonalizedRecipe as any);

  // Recipe detail modal state
  const [selectedRecipe, setSelectedRecipe] = useState<any>(null);
  const [showRecipeDetail, setShowRecipeDetail] = useState(false);
  const [addingToPlan, setAddingToPlan] = useState(false);
  const previewCacheRef = useRef<
    Record<string, PersonalizationPreviewResult>
  >({});
  const [activePreview, setActivePreview] =
    useState<PersonalizationPreviewResult | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const allRecipes = (recipesData as any)?.recipes || [];
  const FULL_INSIGHT_UI = FLAGS.enableFullInsight;
  const personalizedOptions = useMemo(
    () => ({
      limit: 24,
      mealType: selectedCategory || undefined,
      insightMode: FULL_INSIGHT_UI ? "full" : "basic",
    }),
    [selectedCategory, FULL_INSIGHT_UI],
  );
  const { recipes: v2Recipes, isLoading: isLoadingV2 } = usePersonalizedRecipesV2(
    personalizedOptions,
  );

  // Track seen IDs for analytics/debug only (does not affect deck now)
  useEffect(() => {
    setSeenRecipeIds((prev) => {
      const newIds = deck
        .map((r: any) => String(r.id))
        .filter((id: string) => !prev.has(id));
      if (newIds.length === 0) return prev;
      return new Set([...prev, ...newIds]);
    });
  }, [deck]);

  // Track unique count added when deck size changes after category pagination
  const prevDeckSizeRef = useRef(0);
  useEffect(() => {
    if (paginationCategory && deck.length > prevDeckSizeRef.current) {
      const uniqueCountAdded = deck.length - prevDeckSizeRef.current;
      try {
        trackEvent("recipes_load_more_complete", {
          category: paginationCategory,
          uniqueCountAdded,
        });
      } catch {}
    }
    prevDeckSizeRef.current = deck.length;
  }, [deck.length, paginationCategory]);

  const { likedRecipeIds, toggleLikeRecipe } = useLikedRecipes();

  // Business logic: process and filter recipes
  const normalizedTier = normalizeTier(currentTier);

  // Enhanced handler functions
  const refreshRecipes = async () => {
    setRefreshing(true);
    // Reset pagination category and seen IDs to start fresh
    setPaginationCategory(undefined);
    setSeenRecipeIds(new Set());
    setHasMore(true);

    // Small delay to show the refresh animation
    setTimeout(() => setRefreshing(false), 500);
  };

  const handleLoadMore = async () => {
    // Idempotent guard - prevent race conditions
    if (loadingMore) return;
    setLoadingMore(true);

    try {
      // For search queries, show refine filters message instead
      if (searchQuery.trim()) {
        setHasMore(false);
        setLoadingMore(false);
        return;
      }

      // Cycle through different categories to load more recipes
      // This works within the daily cache limits by fetching different categories
      const categories = [
        "main",
        "breakfast",
        "lunch",
        "dessert",
        "snack",
        "soup",
        "salad",
      ];
      const currentCategory = paginationCategory || selectedCategory;
      const currentIndex = categories.indexOf(currentCategory || "");

      // Get next category in cycle, or start with first if not found
      const nextIndex =
        currentIndex >= 0 ? (currentIndex + 1) % categories.length : 0;
      const nextCategory = categories[nextIndex];

      // Track current count before switching categories
      const countBefore = deck.length;

      // Set pagination category to trigger new query
      setPaginationCategory(nextCategory);

      // Track analytics with context
      try {
        trackEvent(Events.RecipesLoadMore, {
          sourceCategory: currentCategory || "none",
          targetCategory: nextCategory,
          currentCount: countBefore,
        });
      } catch {}

      // After a few cycles, disable load more to prevent infinite loading
      if (nextIndex === 0 && currentIndex >= 0) {
        setTimeout(() => setHasMore(false), 1000);
      }

      setTimeout(() => setLoadingMore(false), 500);
    } catch (error) {
      console.error("Failed to load more recipes:", error);
      setLoadingMore(false);
    }
  };

  const handleAddToPlan = async (
    recipeId: string,
    dayIndex: number = 0,
    mealType: string = "dinner",
    preview?: PersonalizationPreviewResult | null,
  ): Promise<boolean> => {
    if (addingToPlan) return false;

    let wasSuccess = false;
    try {
      setAddingToPlan(true);
      trackEvent(Events.RecipeAddToPlan, { recipeId, dayIndex, mealType });

      const today = new Date();
      const currentDayOfWeek = today.getDay();
      const targetDate = new Date(today);
      targetDate.setDate(today.getDate() - currentDayOfWeek + dayIndex);

      let result: any;
      // Attempt DB-personalized path first (if this is a DB recipe id)
      try {
        if (typeof recipeId === "string" && !/^\d+$/.test(recipeId)) {
          const personalizeArgs: any = {
            recipeId,
            dayIndex,
            mealType,
            servings: 2,
            targetDate,
          };
          if (preview?.entry) {
            personalizeArgs.preview = {
              entry: preview.entry,
              learningInsights: preview.learningInsights,
              modifiedRecipe: preview.modifiedRecipe,
            };
          }
          result = await doPersonalizeAdd(personalizeArgs);
        } else {
          throw new Error("not-db-id");
        }
      } catch (e) {
        // Fallback to Spoonacular add
        result = await doAddRecipeToMealPlan({
          recipeId,
          dayIndex,
          mealType,
          servings: 2,
          targetDate,
        });
      }

      if (result && (result as any).success) {
        // Show success message or navigate to plans
        toast.success((result as any).message || "Added to this week's plan");
        console.log("✅ Recipe added to meal plan:", (result as any).message);
        try {
          refetchCurrentPlan?.();
        } catch {}
        try {
          refetchPlans?.();
        } catch {}
        wasSuccess = true;
      }
    } catch (error) {
      console.error("❌ Failed to add recipe to meal plan:", error);
      const msg =
        (error as any)?.message || "Could not add to plan. Please try again.";
      toast.error(msg);
    } finally {
      setAddingToPlan(false);
    }
    return wasSuccess;
  };

  const handleViewDetails = (recipeId: string) => {
    trackEvent(Events.RecipeViewDetails, { recipeId });

    // Find the recipe in the deck
    const recipe = deck.find((r: any) => r.id === recipeId);
    if (recipe) {
      setSelectedRecipe(recipe);
      setShowRecipeDetail(true);
      const cached = previewCacheRef.current[recipeId];
      setActivePreview(cached ?? null);
      setPreviewLoading(false);
    }
  };

  const handleAddToPlanFromModal = async (
    recipeId: string,
    dayIndex?: number,
    mealType?: string,
    preview?: PersonalizationPreviewResult | null,
  ) => {
    const success = await handleAddToPlan(
      recipeId,
      dayIndex ?? 0,
      mealType ?? "dinner",
      preview ?? null,
    );
    if (success) {
      setShowRecipeDetail(false);
    }
  };

  const handlePreviewPersonalization = async (recipeId: string) => {
    if (!recipeId || /^\d+$/.test(recipeId)) return;
    if (previewLoading) return;
    setPreviewError(null);
    const cached = previewCacheRef.current[recipeId];
    if (cached) {
      setActivePreview(cached);
      return;
    }

    setPreviewLoading(true);
    try {
      const response: any = await doPreviewPersonalization({
        recipeId,
        servings: 2,
      });
      if (response && (response as any).success) {
        previewCacheRef.current[recipeId] =
          response as PersonalizationPreviewResult;
        setActivePreview(response as PersonalizationPreviewResult);
      } else {
        throw new Error("Preview failed");
      }
    } catch (error) {
      console.error("Failed to generate personalized preview", error);
      const message =
        (error as any)?.message || "Could not generate personalized preview.";
      setPreviewError(message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleCreateCustomRecipe = () => {
    trackEvent(Events.CreateCustomRecipeClick);
    navigate("/create-recipe");
  };

// ⬇️ Put near other refs
const mounted = React.useRef(false);

// ✅ REPLACE the existing effect with two effects:

// (A) Mount-only telemetry (StrictMode-safe via ref guard)
useEffect(() => {
  if (mounted.current) return;
  mounted.current = true;

  trackEvent("discover_view");
  trace("discover_mount");

  // Do an initial snap based on current deck/error state without subscribing to them
  if (deck.length > 0) {
    trace("deck_fetch_ok", { count: deck.length });
  } else if (error) {
    const message = (error as any)?.message || String(error);
    trace("deck_fetch_err", { message });
  }
  // ⬅ empty deps: runs once in production; in dev StrictMode it's double-invoked,
  // but the ref guard prevents the second execution.
}, []);

// (B) Optional: lightweight observer that reacts to *size* changes only
useEffect(() => {
  // No setState, just telemetry; depends on deck.length and a stable error message
  const message = error ? ((error as any)?.message || String(error)) : null;

  if (deck.length > 0) {
    trace("deck_size_changed", { count: deck.length });
  } else if (message) {
    trace("deck_still_empty", { message });
  }
}, [deck.length, error && ((error as any)?.message || String(error))]);

  const handleTrack = async (
    recipeId: string,
    action: "liked" | "skipped" | "saved" | "maybe",
  ) => {
    const recipe =
      (deck || []).find((r: any) => String(r?.id) === String(recipeId)) || {};
    const title = recipe?.title || recipe?.name || "";
    const cuisine =
      recipe?.cuisine ||
      recipe?.tags?.find((t: string) => /cuisine/i.test(t)) ||
      "";
    const difficulty = recipe?.difficulty || "easy";
    const prepTime = recipe?.prepTime || recipe?.readyInMinutes || 0;
    const calories = recipe?.calories || recipe?.nutrition?.calories || 0;
    const tags = Array.isArray(recipe?.tags) ? recipe.tags : [];
    try {
      // Privacy-first client event
      if (action === "liked") trackEvent("swipe_like", { recipeId });
      if (action === "skipped") trackEvent("swipe_skip", { recipeId });
      if (action === "liked") trace("swipe_like", { id: recipeId });
      if (action === "skipped") trace("swipe_skip", { id: recipeId });
      await waspApi.trackSwipe({
        userId: String(user?.id || "anon"),
        sessionId: "v32",
        recipeId,
        action,
        recipe: { title, cuisine, difficulty, prepTime, calories, tags },
        swipeMetrics: { swipeSpeed: 0, swipeDistance: 0, hesitation: false },
        contextData: {
          timeOfDay: "",
          previousSwipes: 0,
          sessionDuration: 0,
          screenSize: { width: 0, height: 0 },
        },
        timestamp: Date.now(),
      } as any);
    } catch (e) {
      console.error("Swipe track failed", e);
    }
  };

  const handleCreatePlan = async (selected: any[]) => {
    if (creating) return;
    try {
      setCreating(true);
      const selectedCount = selected?.length ?? 0;
      trackEvent("discover_create_plan_click", { selectedCount });
      trace("create_click", { selectedCount });
      if (selectedCount < 6) {
        trace("generate_err", {
          message: "min_selected_not_met",
          selectedCount,
        });
        toast("Select at least 6 recipes to build a balanced plan.");
        setCreating(false);
        return;
      }
      trace("generate_start");
      // For now we don't pass recipes; server uses profile context to generate.
      await doGeneratePlan({});
      trace("generate_ok");
      // Navigate to plans to reflect latest history/metrics.
      navigate("/plans");
      trace("nav_to_plans");
    } catch (e) {
      console.error("Create plan from discover failed", e);
      trace("generate_err", { message: (e as any)?.message || String(e) });
    } finally {
      setCreating(false);
    }
  };

  // Grid view handlers for desktop
  const handleLikeRecipe = (recipeId: string) => {
    const recipe = deck.find((r: any) => r.id === recipeId);
    if (recipe) {
      toggleLikeRecipe(
        {
          id: recipe.id,
          title: recipe.title || recipe.name,
          imageUrl: recipe.image || recipe.imageUrl || recipe.heroImageUrl,
          difficulty: recipe.difficulty,
          cuisine: recipe.cuisine,
        },
        {
          prepTime:
            recipe.prepTime ||
            recipe.readyInMinutes ||
            recipe.totalMinutes ||
            recipe.cookTime ||
            undefined,
          calories: recipe.calories || recipe?.nutrition?.calories || recipe?.nutritionInfo?.calories || undefined,
          tags: Array.isArray(recipe.tags) ? recipe.tags : (Array.isArray(recipe.dietTags) ? recipe.dietTags : []),
        }
      );
      handleTrack(recipeId, "liked");
    }
  };

  const handleCreatePlanFromGrid = async () => {
    const selectedRecipes = deck.filter((recipe: any) =>
      likedRecipeIds.includes(recipe.id),
    );
    await handleCreatePlan(selectedRecipes);
  };

  // Computed values
  const currentMealPlanRecipes = currentMealPlan?.recipes || [];
  const hasCurrentMealPlan = !!currentMealPlan && currentMealPlanRecipes.length > 0;
  const isRecipeInMealPlan = useCallback((recipeId: string) => {
    return currentMealPlanRecipes.some(recipe => recipe.id === recipeId);
  }, [currentMealPlanRecipes]);

  // Extract unique categories and tags for filters
  const availableCategories = useMemo(() => {
    if (!deck) return [];
    const categories = new Set(deck.map(recipe => recipe.category).filter(Boolean));
    return Array.from(categories).sort();
  }, [deck]);

  const availableTags = useMemo(() => {
    if (!deck) return [];
    const tags = new Set();
    deck.forEach(recipe => {
      recipe.tags?.forEach(tag => tags.add(tag));
    });
    return Array.from(tags).sort() as string[];
  }, [deck]);

  // Map all props to the view with extended functionality for RecipePage compatibility
  const handleSetupProfile = () => {
    navigate("/onboarding");
  };

  const viewProps = {
    // Data
    recipes: deck,
    currentMealPlan,
    currentMealPlanRecipes,
    userProfile,
    userTasteProfile,
    userName,
    normalizedTier,
    
    // Loading states
    recipesLoading: loading || isLoadingV2,
    
    // Search and filter state
    searchQuery,
    selectedCategory,
    selectedTags: selectedDietTags,
    sortBy: 'relevance' as const,
    viewMode: 'grid' as const,
    currentPage: page,
    hasMoreRecipes: hasMore,
    
    // Filter options
    availableCategories,
    availableTags,
    
    // Modal state
    selectedRecipe,
    replaceModalOpen: false,
    removeModalOpen: false,
    detailsModalOpen: showRecipeDetail,
    recipeToReplace: null,
    recipeToRemove: null,
    
    // Computed values
    hasCurrentMealPlan,
    isRecipeInMealPlan,
    totalRecipesCount: deck.length,
    
    // Extended state for original RecipePage compatibility
    refreshing,
    paginationCategory,
    loadingMore,
    creating,
    addingToPlan,
    likedRecipes: likedRecipeIds,
    activePreview,
    previewLoading,
    previewError,
    hasMore,
    error,
    personalizedRecipes: v2Recipes,
    personalizedLoading: isLoadingV2,
    
    // Event handlers - Search and filter
    onSearch: setSearchQuery,
    onCategoryFilter: setSelectedCategory,
    onTagFilter: setSelectedDietTags,
    onSortChange: () => {},
    onViewModeChange: () => {},
    onLoadMore: handleLoadMore,
    
    // Event handlers - Recipe interactions
    onRecipeClick: handleViewDetails,
    onReplaceRecipe: () => {},
    onConfirmReplace: () => {},
    onRemoveRecipe: () => {},
    onConfirmRemove: () => {},
    onAddToMealPlan: handleAddToPlan,
    onGenerateNewPlan: () => doGeneratePlan({}),
    
    // Event handlers - Modal management
    onCloseModals: () => setShowRecipeDetail(false),
    
    // Event handlers - Navigation
    onGoToDashboard: () => navigate('/'),
    onGoToShoppingList: () => navigate('/shopping-list'),
    onSetupProfile: handleSetupProfile,
    
    // Extended event handlers for original RecipePage compatibility
    onRefreshRecipes: refreshRecipes,
    onTrack: handleTrack,
    onCreatePlan: handleCreatePlan,
    onLikeRecipe: handleLikeRecipe,
    onCreatePlanFromGrid: handleCreatePlanFromGrid,
    onAddToPlanFromModal: handleAddToPlanFromModal,
    onPreviewPersonalization: handlePreviewPersonalization,
    onCreateCustomRecipe: handleCreateCustomRecipe,
    onViewDetails: handleViewDetails,
  };

  return <RecipePageView {...viewProps} />;
}

// Export types for the view component
export type { Recipe, MealPlan, UserProfile };
// @ts-nocheck
